import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Search, FileDown, BarChart3, Trophy, MapPin, Brain, FileText, TrendingUp, Layers, LineChart, Users, ShieldCheck, Eye } from "lucide-react";
import { motion } from "framer-motion";
import { getLADirectory } from "../services/api";
import NationalRolloutWaitlist from "../components/NationalRolloutWaitlist";

const FEATURES = [
  { icon: Sparkles, title: "AI Query, no hallucination", body: "Ask any infrastructure question in plain English. Every answer is grounded in verified Ofcom data with one-click citations." },
  { icon: Search, title: "Gigabit Postcode Checker", body: "Instantly see if any UK postcode meets gigabit availability — and export the result as a council-ready PDF." },
  { icon: FileDown, title: "Authority Report Generator", body: "Generate Authority, District or Governance-grade reports in seconds. No analyst required." },
  { icon: BarChart3, title: "Compare Authorities", body: "Pick any LAs — see who leads on FDDI, gigabit, DIP, and where the gaps live." },
];

const HIGHLIGHTS = [
  { rank: "01", la: "South Tyneside", metric: "Full Fibre", delta: "+44.5pp", note: "22.6% → 67.1% over 2 years — largest regional jump." },
  { rank: "02", la: "Gateshead",      metric: "Full Fibre", delta: "+34.0pp", note: "45.6% → 79.6% — strong sustained rollout." },
  { rank: "03", la: "Middlesbrough",  metric: "Full Fibre", delta: "+27.4pp", note: "36.3% → 63.7% — closing the gap fast." },
];

export default function StandardLanding() {
  const [directory, setDirectory] = useState([]);
  useEffect(() => {
    getLADirectory().then((d) => setDirectory(d.items || [])).catch(() => {});
  }, []);
  return (
    <div className="font-body">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 pt-14 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#5C5C59] mb-4 flex items-center gap-2">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full rounded-full bg-brand-accent opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-accent"></span>
              </span>
              Connectivity Impact Forecaster · North East Live
            </div>
            <h1 className="font-heading font-bold tracking-tight text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] text-brand">
              Managing digital infrastructure is hard.<br />
              Measuring socioeconomic impact is complex.<br />
              <span className="text-brand-accent">Infralytics makes it simple.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C5C59] max-w-xl leading-relaxed">
              The data-driven <span className="text-brand font-medium">Connectivity Impact
              Forecaster</span> that helps public bodies see the{" "}
              <span className="text-brand font-medium">risk and the opportunity</span> in every
              postcode — quantify what earlier initiatives actually delivered, and build the
              investment case for your Local or Combined Authority in minutes, not months.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono uppercase tracking-wider text-[#5C5C59]">
              <span>Mobile · 3G / 4G / 5G / 6G</span>
              <span>Fixed · FTTP / FTTC / DOCSIS / FWA / Satellite</span>
              <span>PSTN retirement</span>
              <span>Data centres</span>
              <span>IMD deprivation</span>
              <span>+ knowledge graph</span>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/ai"
                data-testid="hero-ai-cta"
                className="inline-flex items-center gap-2 bg-brand text-white rounded-md px-6 py-3 text-sm font-medium hover:bg-brand-deep transition-colors"
              >
                Ask the AI <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/postcode"
                data-testid="hero-postcode-cta"
                className="inline-flex items-center gap-2 border border-brand text-brand rounded-md px-6 py-3 text-sm font-medium hover:bg-brand-bg transition-colors"
              >
                Check a postcode
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative aspect-square bg-white border border-brand-border rounded-md shadow-sm overflow-hidden">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/9ec4bfd4-e10f-4c55-9f55-3f43d4289692/images/1c3cc261cbf55604f44aaa74e1ee230bdbc2e7c0aaacc7e486efd4b121a3d948.png"
                alt="Topographic data visualisation"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md border border-brand-border rounded-md p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C59] mb-1">Connected Nations · Spring 2026</div>
                <div className="font-heading font-bold text-2xl text-brand">72,388 postcodes</div>
                <div className="text-xs text-[#5C5C59]">12 NE England LAs · 3 vintages tracked</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECONDARY BRAND RIBBON — original positioning retained as brand comms */}
      <section className="border-y border-slate-800 bg-[#061426] text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-7 grid gap-6 md:grid-cols-[1fr_auto] items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-accent mb-1.5">
              Understand your digital infrastructure · Data made simple
            </div>
            <p className="text-[15px] text-white/85 leading-relaxed max-w-3xl">
              The AI-driven intelligence platform replacing consultancy hours and analyst overhead —
              currently live across the{" "}
              <span className="text-white font-medium">North East of England</span> for Local
              Authorities, Combined Authorities, DSIT, investors, operators and AltNets. National
              rollout follows.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 md:flex-col md:items-end text-[10px] font-mono uppercase tracking-wider text-white/60">
            <span>12 NE LAs live</span>
            <span>3 Ofcom vintages tracked</span>
            <span>HM Green Book aligned</span>
          </div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="bg-brand-bgAlt border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-6 pt-10 pb-14">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#5C5C59] mb-3">Risk · Opportunity · Investment case</div>
          <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-[34px] leading-[1.2] text-brand max-w-4xl">
            See the risk. See the opportunity. Quantify the impact of every pound already spent —
            and <span className="text-brand-accent">make the investment case for the next one</span> with
            postcode-level evidence your board and scrutiny committee can defend.
          </h2>
          <p className="mt-3 text-sm text-[#5C5C59] max-w-3xl leading-relaxed">
            Infralytics helps decision-makers understand their digital infrastructure, forecast
            future outcomes, and identify the next best action through AI-powered policy-grade
            intelligence — defensible enough for boards, and rigorous enough for BDUK, UKSPF and
            Green Book scrutiny.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white border border-brand-border rounded-md p-8 hover:-translate-y-1 hover:border-brand transition-all"
                >
                  <Icon className="h-6 w-6 text-brand-accent mb-4" strokeWidth={1.5} />
                  <div className="font-heading font-semibold text-lg text-[#111110] mb-2">{f.title}</div>
                  <p className="text-sm text-[#5C5C59] leading-relaxed">{f.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HIGHLIGHT REEL TEASER */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#5C5C59] mb-3 flex items-center gap-2">
              <Trophy className="h-3.5 w-3.5 text-brand-accent" /> Highlight Reel · This month
            </div>
            <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-brand max-w-2xl">
              Leaders are setting the pace. Where does your authority rank?
            </h2>
          </div>
          <Link
            to="/highlights"
            className="inline-flex items-center gap-1.5 text-brand text-sm font-medium hover:gap-2.5 transition-all"
            data-testid="highlights-cta"
          >
            See the full leaderboard <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {HIGHLIGHTS.map((h) => (
            <motion.div
              key={h.rank}
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="bg-white border border-brand-border rounded-md p-8 relative overflow-hidden"
            >
              <div className="font-heading font-bold text-[88px] leading-none text-brand-bgAlt absolute -top-3 -right-2 select-none">
                {h.rank}
              </div>
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C5C59]">{h.metric}</div>
                <div className="font-heading font-bold text-2xl text-brand mt-1">{h.la}</div>
                <div className="font-mono text-3xl text-brand-accent mt-3 font-medium">{h.delta}</div>
                <p className="text-sm text-[#5C5C59] mt-3 leading-relaxed">{h.note}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COUNCIL DIRECTORY */}
      {directory.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 py-16" data-testid="council-directory">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#5C5C59] flex items-center gap-2">
                <MapPin className="h-3 w-3" /> Council pages
              </div>
              <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl text-brand">How every authority is doing</h2>
              <p className="mt-2 text-[#5C5C59] max-w-2xl">Each council has its own page — live rank, 3-year trajectory, peer comparison, and goals to unlock.</p>
            </div>
            <Link to="/compare" className="text-sm text-brand-accent hover:text-brand inline-flex items-center gap-1" data-testid="council-compare-link">
              Compare side-by-side <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {directory.map((d) => (
              <Link
                key={d.slug}
                to={`/la/${d.slug}`}
                className="group rounded-xl border border-brand-border bg-white p-4 hover:border-brand-accent transition-colors"
                data-testid={`council-card-${d.slug}`}
              >
                <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wider ${
                  d.band === "Leader" ? "bg-emerald-50 text-emerald-700" :
                  d.band === "Mature" ? "bg-sky-50 text-sky-700" :
                  d.band === "Developing" ? "bg-amber-50 text-amber-700" :
                  d.band === "Critical Gap" ? "bg-rose-50 text-rose-700" : "bg-slate-50 text-slate-500"
                }`}>{d.band}</div>
                <div className="mt-2 font-heading font-semibold text-brand text-base leading-tight group-hover:text-brand-accent transition-colors">{d.name}</div>
                {d.fddi != null && <div className="mt-1 text-xs text-[#5C5C59] font-mono">FDDI {(d.fddi * 100).toFixed(1)}%</div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="bg-brand text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 mb-3">Replace your consultancy spend</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl leading-tight">
              Real-time, policy-grade insight — for a fraction of an analyst&apos;s salary.
            </h2>
          </div>
          <Link
            to="/signup"
            data-testid="cta-signup"
            className="inline-flex items-center gap-2 bg-brand-accent text-white rounded-md px-8 py-4 text-sm font-medium hover:bg-[#C84D0E] transition-colors"
          >
            Start free trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* NATIONAL ROLLOUT WAITLIST */}
      <NationalRolloutWaitlist />

      {/* TOP-3 BROADBAND PICKS — pre-footer marketing band */}
      <TopBroadbandPicks />

      {/* ABOUT — Infrastructure Intelligence */}
      <AboutInfralytics />
    </div>
  );
}

// ── About Infralytics — sandwiched between green CTA and footer ─────────────

// TopBroadbandPicks — editorial top-3 picks per category, sourced from
// ThinkBroadband / ISPReview / Broadband Genie.  Uses logo tiles hydrated
// from the /api/isps directory so brand marks stay consistent with the
// LA-page gallery.
const TOP_PICK_CATEGORIES = [
  {
    key: "builders",
    title: "Largest FTTP network builders",
    blurb: "Physical fibre reach across UK premises passed.",
    source: "ThinkBroadband UK Broadband Report · March 2025",
    picks: ["openreach-fttp", "cityfibre", "nexfibre"],
  },
  {
    key: "altnets",
    title: "Best consumer altnets",
    blurb: "Highest-rated challenger networks for reliability and value.",
    source: "ISPReview Best ISP Awards · Broadband Genie ratings",
    picks: ["youfibre", "community-fibre", "hyperoptic"],
  },
  {
    key: "retail",
    title: "Most popular FTTP retailers",
    blurb: "Household-name resellers over Openreach's wholesale network.",
    source: "Ofcom retail-share data · Broadband Choices",
    picks: ["bt-retail", "sky", "cuckoo"],
  },
  {
    key: "mobile",
    title: "UK mobile networks by coverage",
    blurb: "4G/5G nationwide leaders (no regional MNOs exist in the UK).",
    source: "RootMetrics UK · Ofcom Connected Nations mobile",
    picks: ["ee-mno", "vodafone-mno", "o2-mno"],
  },
];

function TopBroadbandPicks() {
  const [byId, setById] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/isps?limit=500`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        const map = {};
        for (const i of d.items || []) map[i.isp_id] = i;
        setById(map);
        setReady(true);
      })
      .catch(() => setReady(true));
  }, []);

  return (
    <section
      className="border-t border-brand-border bg-white"
      data-testid="top-broadband-picks"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent mb-2">
              Editorial picks · Independent, unpaid
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-brand max-w-3xl leading-tight">
              The best of UK broadband, at a glance.
            </h2>
            <p className="mt-2 text-sm text-[#5C5C59] max-w-2xl leading-relaxed">
              Curated top-3 lists across the four connectivity buckets that matter. Cross-referenced
              from ThinkBroadband, ISPReview, Broadband Genie and Ofcom — no paid placements,
              refreshed with every Ofcom vintage.
            </p>
          </div>
          <Link
            to="/la/newcastle"
            data-testid="top-picks-explore"
            className="inline-flex items-center gap-1 rounded-md border border-brand-border px-4 py-2 text-[11px] font-mono uppercase tracking-wider text-brand hover:border-brand-accent hover:text-brand-accent transition"
          >
            See every operator in your LA →
          </Link>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TOP_PICK_CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              data-testid={`top-pick-${cat.key}`}
              className="rounded-xl border border-brand-border bg-brand-bg/40 p-4 flex flex-col"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-accent mb-1">
                {cat.title}
              </div>
              <p className="text-[11px] text-[#5C5C59] mb-3 leading-relaxed">{cat.blurb}</p>
              <ol className="space-y-2 mb-3">
                {cat.picks.map((id, idx) => {
                  const isp = byId[id];
                  const logo = isp?.logo_url;
                  const name = isp?.name || id;
                  return (
                    <li
                      key={id}
                      data-testid={`top-pick-${cat.key}-${id}`}
                      className="flex items-center gap-2.5 rounded-md bg-white border border-brand-border p-2"
                    >
                      <span className="w-5 text-center font-mono text-[11px] font-bold text-brand-accent">
                        {idx + 1}
                      </span>
                      {logo ? (
                        <img
                          src={logo}
                          alt={name}
                          className="w-8 h-8 rounded object-contain bg-white"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded bg-brand-border flex items-center justify-center text-[10px] font-mono text-[#5C5C59]">
                          {name
                            .split(/[\s&.-]+/)
                            .slice(0, 2)
                            .map((s) => s[0])
                            .join("")
                            .toUpperCase()}
                        </div>
                      )}
                      <span className="text-sm text-brand font-medium truncate">
                        {ready ? name : "—"}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <div className="mt-auto pt-2 border-t border-brand-border">
                <div className="font-mono text-[9px] uppercase tracking-wider text-[#5C5C59]">
                  Source
                </div>
                <div className="text-[10px] text-[#5C5C59] leading-tight mt-0.5">
                  {cat.source}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-[10px] font-mono text-[#5C5C59] leading-relaxed">
          Not affiliated with, endorsed by, or paid by any listed operator. Rankings reflect
          publicly available data at time of publication and update on Ofcom / ThinkBroadband
          release cadence. Availability varies by postcode — always confirm with the operator.
        </p>
      </div>
    </section>
  );
}

const TRANSLATION_CHAIN = [
  { n: "01", q: "What are we looking at?", a: "The infrastructure, service, investment, or community outcome being measured." },
  { n: "02", q: "What happened?",          a: "The action, intervention, or investment that took place." },
  { n: "03", q: "What changed?",           a: "The measurable outcome as a direct result." },
  { n: "04", q: "Why does it matter?",     a: "What it means for residents, businesses, investors, and policymakers." },
  { n: "05", q: "What happens next?",      a: "What will occur if current trends continue, investment increases, or intervention stops." },
];

const CAPABILITIES = [
  { icon: Brain,       title: "Digital Infrastructure Intelligence", body: "Live, structured understanding of how infrastructure performs at community level — not just what's theoretically available." },
  { icon: FileText,    title: "Policy-Grade Reporting",              body: "Outputs designed for decision-makers. Built to inform policy, support funding bids, and survive scrutiny." },
  { icon: TrendingUp,  title: "Investment Impact Assessment",         body: "Pre-deployment intelligence that tells investors what deploying capital will actually change." },
  { icon: Layers,      title: "Coverage & Adoption Analysis",         body: "The gap between what's available and what's actually used is where the real story lives. We measure both." },
  { icon: LineChart,   title: "Economic & Social Value Measurement",  body: "Quantifying what digital infrastructure means for productivity, employment, and community resilience." },
  { icon: Eye,         title: "Scenario Modelling & Forecasting",     body: "Forward-looking intelligence: what happens next under different investment, policy, and intervention conditions." },
  { icon: Users,       title: "Community Outcome Evaluation",         body: "Moving beyond network metrics to measure real impact on the people connectivity is supposed to serve." },
  { icon: ShieldCheck, title: "Green Book–Aligned Decision Support",  body: "Every output is structured for the evidence requirements of public investment appraisal." },
];

function AboutInfralytics() {
  return (
    <section className="bg-brand-bg border-t border-brand-border" data-testid="about-infralytics">
      <div className="max-w-[1400px] mx-auto px-6 py-24 sm:py-28">
        {/* Tagline + headline marquee */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-20 sm:mb-24"
          data-testid="about-tagline"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent">
              Data Made Simple<span className="align-super text-[10px]">™</span>
            </span>
            <span className="h-px flex-1 max-w-[120px] bg-brand-accent/40" />
            <span className="font-mono text-xs uppercase tracking-[0.22em] text-brand">
              Intelligence Made Actionable
            </span>
          </div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] text-brand max-w-5xl">
            Turning <span className="text-brand-accent">data</span> into policy,
            investment and community outcomes.
          </h2>
        </motion.div>

        {/* Eyebrow + lead */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent mb-4">
              About Infralytics
            </div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-brand">
              Not coverage.<br />
              <span className="text-brand-accent">Consequence.</span>
            </h2>
            <p className="mt-6 text-[#5C5C59] text-base sm:text-lg leading-relaxed">
              The world&apos;s most important infrastructure is the least understood. Coverage statistics tell you what is <em>available</em>. They tell you almost nothing about what is actually happening.
            </p>
            <p className="mt-4 text-[#5C5C59] text-base sm:text-lg leading-relaxed">
              Infralytics is the world&apos;s first AI-powered digital infrastructure consultant — a platform that transforms complex, fragmented data into <span className="text-brand font-medium">policy-grade intelligence</span> that enables better decisions, smarter investments, and measurable community outcomes.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-brand bg-white border border-brand-border rounded-full px-4 py-2">
              <Sparkles className="h-3 w-3 text-brand-accent" /> Data Made Simple<span className="text-brand-accent">™</span>
            </div>
          </motion.div>

          {/* Translation chain */}
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
              <div className="px-6 sm:px-8 py-5 border-b border-brand-border bg-brand-bgAlt/40">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-accent mb-1">
                  Every dataset · one coherent narrative
                </div>
                <div className="font-heading font-semibold text-brand text-lg">
                  Five questions. Five answers. One decision.
                </div>
              </div>
              <ol className="divide-y divide-brand-border">
                {TRANSLATION_CHAIN.map((step) => (
                  <li key={step.n} className="px-6 sm:px-8 py-5 flex gap-5 sm:gap-6 hover:bg-brand-bg/40 transition-colors group">
                    <span className="font-mono text-[11px] tracking-[0.18em] text-brand-accent w-8 shrink-0 pt-0.5 group-hover:translate-x-[2px] transition-transform">
                      {step.n}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-brand text-base sm:text-[17px]">
                        {step.q}
                      </div>
                      <div className="mt-1 text-sm text-[#5C5C59] leading-relaxed">
                        {step.a}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <p className="mt-5 text-xs text-[#5C5C59] leading-relaxed max-w-2xl">
              This is what we mean by Data Made Simple™. Not simplification — <span className="text-brand font-medium">translation</span>, from raw technical data into the language of consequence.
            </p>
          </motion.div>
        </div>

        {/* Capability strip */}
        <div className="mt-20 sm:mt-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent mb-2">What Infralytics delivers</div>
              <h3 className="font-heading font-bold text-2xl sm:text-3xl text-brand max-w-2xl leading-tight">
                Eight capabilities. One intelligence layer.
              </h3>
            </div>
            <p className="text-sm text-[#5C5C59] max-w-md leading-relaxed">
              Every output is built to inform policy, support funding bids, and survive scrutiny — aligned with HM Treasury Green Book principles.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAPABILITIES.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.04 }}
                  className="group bg-white border border-brand-border rounded-lg p-5 hover:border-brand-accent hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`capability-${i}`}
                >
                  <Icon className="h-5 w-5 text-brand-accent mb-3 group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                  <div className="font-heading font-semibold text-brand text-[15px] leading-tight mb-2">
                    {cap.title}
                  </div>
                  <p className="text-xs text-[#5C5C59] leading-relaxed">{cap.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mission card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 bg-brand text-white rounded-xl px-6 sm:px-12 py-12 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
        >
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-brand-accent mb-3">The mission</div>
            <h3 className="font-heading font-bold text-3xl sm:text-4xl leading-[1.05]">
              Make digital infrastructure <span className="text-brand-accent">understandable, measurable, and actionable.</span>
            </h3>
            <p className="mt-5 text-white/75 text-base leading-relaxed max-w-2xl">
              Transforming complex data into intelligence that drives better policy, smarter investment, and better outcomes for communities.
            </p>
          </div>
          <div className="lg:col-span-5 lg:border-l lg:border-white/15 lg:pl-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">A product of</div>
            <div className="font-heading font-bold text-2xl mb-2">Nexintell Limited</div>
            <p className="text-sm text-white/70 leading-relaxed mb-5">
              UK GovTech &amp; SaaS company based in Stockton-on-Tees, North East England — building the intelligence layer for global digital infrastructure.
            </p>
            <a
              href="https://www.nexintell.co"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nexintell-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-accent hover:gap-3 transition-all"
            >
              Learn more about Nexintell <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
